import { User, UserAuth } from "../shared/interfaces/Auth";
import { Client } from "./Clients";

class AuthService {
    private client: Client;
    private notification: {
        success: (message: string) => void;
        error: (message: string) => void;
    };

    constructor(client: Client, notification: any) {
        this.client = client;
        this.notification = notification;
    }

    isAuthenticated(): boolean {
        return !!localStorage.getItem("authToken");
        // return true;
    }

    hasRole(role: string): boolean {
        const userRole = localStorage.getItem("userRole");
        return userRole === role;
    }

    invalidateToken(token: string): void {
        console.log("Token invalidated:", token);
    }

    hasPermission = (requiredPermission: string[]): boolean => {
        // Chukua permissions from localStorage and parse them into an array
        const storedPermissions: string[] = JSON.parse(localStorage.getItem("permissions") || "[]");

        // Check if every required permission is included in the stored permissions
        // return requiredPermission.every((permission) =>
        //     storedPermissions.includes(permission)
        // );

        // Baadhi
        return requiredPermission.some((permission) =>
            storedPermissions.includes(permission)
        );
    };

    logout() {
        this.clearStore();
        window.location.href = "/login";
    }

    private setTokensAndCookies(userAuth: UserAuth) {
        if (!userAuth) throw new Error("Invalid authentication token.");
        localStorage.setItem("authToken", userAuth?.["auth-token"]?.["access-token"]);
        localStorage.setItem("refreshToken", userAuth?.["auth-token"]?.["refresh-token"]);

        //
        const permissions = userAuth?.["user-acl"]?.["permissions"];
        localStorage.setItem("permissions", JSON.stringify(permissions));

        const roles = userAuth?.["user-acl"]?.["roles"];
        localStorage.setItem("roles", JSON.stringify(roles));
    }

    private getACLKey(email: string): string {
        return `ACL_${email}`;
    }

    private storeCache(key: string, value: any) {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch (error) {
            console.error("Error storing cache:", error);
            throw new Error("Failed to store user data.");
        }
    }

    private monitorRefreshToken() {
        const expiry = Number(localStorage.getItem("expiry"));
        const refreshTime = expiry - Date.now();
        if (refreshTime > 10000) {
            setTimeout(() => {
                if (this.isAuthenticated()) {
                    this.refreshToken();
                }
            }, refreshTime - 10000);
        }
    }

    private clearStore() {
        localStorage.clear();
        sessionStorage.clear();
    }

    async refreshToken() {
        const endpoint = "/oauth2/access_token";

        const token = localStorage.getItem("authToken");
        const refresh = localStorage.getItem("refreshToken");
        if (!token || !refresh) return;

        try {
            const body = new URLSearchParams();
            body.append("refresh_token", refresh);
            body.append("grant_type", "refresh_token");

            const response = await this.client.post(endpoint, body.toString());

            if (response.data?.access_token) {
                localStorage.setItem("authToken", response.data.access_token);
                localStorage.setItem("refreshToken", response.data.refresh_token);
                localStorage.setItem("expiry", (Date.now() + response.data.expires_in * 1000).toString());
                this.monitorRefreshToken();
                return response.data;
            }
        } catch (error) {
            console.error("Failed to refresh token", error);
        }
    }

    async generateToken(emailAddress: string): Promise<string> {
        const expiry = Date.now() + 60 * 60 * 24 * 1000; // 24 hours expiration
        return btoa(`${emailAddress}.${expiry}`);
    }

    async validateToken(token: string): Promise<string> {
        const decoded = atob(token).split(".");
        const emailAddress = decoded[0];
        const expiry = Number(decoded[1]);

        if (Date.now() > expiry) throw new Error("Token expired.");
        return emailAddress;
    }

    async sendMail(to: string, subject: string, template: string, context: object): Promise<void> {
        console.log("Email sent to:", to);
        console.log("Subject:", subject);
        console.log("Template:", template);
        console.log("Context:", context);
    }

    async login(user: { email: string; password: string }): Promise<{ success: boolean; message: string }> {
        const endpoint = "/auth/login";
        this.clearStore();
        try {
            if (!user.email || !user.password) {
                throw new Error("Please enter a valid email and password.");
            }

            const response = await this.client.post(endpoint, user);

            if (!response.data || response.code !== 200) {
                throw new Error(response.data.error || "Error occurred while logging in.");
            }
            const userAuth: UserAuth = response.data;

            if (!userAuth.user.is_email_verified) {
                throw new Error("Please verify your email address. Check the spam folder if it's not in the inbox.");
            }

            this.setTokensAndCookies(userAuth);
            console.log(userAuth)
            localStorage.setItem("userName", userAuth?.["user"]?.["name"] ?? "");
            localStorage.setItem("userID", userAuth?.["user"]?.["id"].toString() ?? "");
            localStorage.setItem("userEmail", userAuth?.["user"]?.["email"]);

            const aclKey = this.getACLKey(user.email);
            this.storeCache(aclKey, userAuth);

            return { success: true, message: "Welcome, Login successful." };
        } catch (error: any) {
            this.notification.error(error.message);
            return { success: false, message: error };
        }
    }



 

    async resetPassword(data: { token: string; password: string }): Promise<void> {
        const endpoint = "/users/reset-password-by-email";

        try {
            const { token, password } = data;
            const emailAddress = await this.validateToken(token);
            if (!emailAddress) throw new Error("Invalid token or expired.");

            const response = await this.client.post(endpoint, { email: emailAddress, password });

            if (!response.data) throw new Error("Failed to reset password.");
            this.invalidateToken(token);
            this.notification.success("Password was changed successfully.");
        } catch (err) {
            console.error("Error resetting password:", err);
            this.notification.error("Sorry, your password could not be reset.");
        }
    }

    async resetPasswordAdmin(user: User): Promise<void> {
        const endpoint = "/users/forgot-password";

        try {
            const response = await this.client.post(endpoint, user);

            if (!response.data) throw new Error("Error! Could not update password.");
            this.notification.success("Password updated successfully.");
        } catch (err) {
            console.error("Error resetting password by admin:", err);
            this.notification.error("Error! Could not update password.");
        }
    }

}

export default AuthService;